import Box from '@mui/material/Box';
import { observer } from 'mobx-react-lite';

import { AiReview } from '~hooks/useAiReview/models';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';

import AiReviewIndicator, {
  badgeSizes,
  DismissedAiReviewIndicator,
  NoAiReviewIndicator,
} from '../Files/components/LoadsTable/components/AiReviewIndicator';
import FlaggedTicketIndicator from '../Files/components/LoadsTable/components/FlaggedTicketIndicator';

interface LoadReviewIndicatorProps {
  aiReview: Nullable<AiReview>;
  canDismiss?: boolean;
  isTicketFlagged: boolean;
  onDismiss?: () => Promise<void>;
  onUndoDismiss?: () => Promise<void>;
}

export default observer(function LoadReviewIndicator({
  aiReview,
  canDismiss = false,
  isTicketFlagged,
  onDismiss,
  onUndoDismiss,
}: LoadReviewIndicatorProps) {
  const { userStore } = useStores();

  if (isTicketFlagged) {
    return <FlaggedTicketIndicator />;
  }

  if (aiReview?.dismissedAt) {
    return (
      <DismissedAiReviewIndicator
        aiReview={aiReview}
        canUndo={canDismiss}
        onUndoDismiss={onUndoDismiss}
      />
    );
  }

  if (aiReview) {
    return (
      <AiReviewIndicator
        aiReview={aiReview}
        canDismiss={canDismiss}
        onDismiss={onDismiss}
      />
    );
  }

  // A company that never runs AI reviews would otherwise see the "no review" badge on every
  // load, explaining an absence it has no reason to expect.
  if (userStore.userCompany.aiReviewLoads) {
    return <NoAiReviewIndicator />;
  }

  return (
    <Box
      data-test-id="load-review-indicator-placeholder"
      sx={{ height: badgeSizes.md.box, width: badgeSizes.md.box }}
    />
  );
});
